import { Box, Chip, Typography } from '@mui/material'
import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { SaveStatus } from '../../types'

type SaveStatusRowProps = {
  name: string
  status: SaveStatus
  errorMessage?: string
  /** Custom label to display in the loading status chip instead of the default one. */
  loadingLabel?: string
  /** Custom label to display in the complete status chip instead of the default one. */
  completeLabel?: string
  disabled: boolean
}

export const SaveStatusRow: React.FC<SaveStatusRowProps> = ({ name, errorMessage, status, loadingLabel, completeLabel, disabled }) => {
  const { t } = useCustomTranslation()

  return (
    <Box
      sx={{
        p: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
      }}
    >
      <div>
        <Typography>{name}</Typography>

        {errorMessage && (
          <Typography pl={2} color="error">
            {`${t('common.error')}: ${errorMessage}`}
          </Typography>
        )}
      </div>

      <SaveStatusChip status={status} loadingLabel={loadingLabel} completeLabel={completeLabel} disabled={disabled} />
    </Box>
  )
}

type SaveStatusChipProps = {
  status: SaveStatus
  /** Custom label to display in the loading status chip instead of the default one. */
  loadingLabel: string | undefined
  /** Custom label to display in the complete status chip instead of the default one. */
  completeLabel: string | undefined
  disabled: boolean
}

const SaveStatusChip: React.FC<SaveStatusChipProps> = ({ status, loadingLabel, completeLabel, disabled }) => {
  const { t } = useCustomTranslation()

  switch (status) {
    case 'idle':
      return <Chip label={t('installation.save.status.waiting')} color="info" disabled={disabled} />
    case 'error':
      return <Chip label={t('installation.save.status.error')} color="error" disabled={disabled} />
    case 'loading':
      return <Chip label={loadingLabel ?? t('installation.save.status.saving')} color="secondary" disabled={disabled} />
    case 'complete':
      return <Chip label={completeLabel ?? t('installation.save.status.saved')} color="primary" disabled={disabled} />
  }
}
